/**
 * Click-and-drag scrolling for horizontal rails.
 *
 * Trackpads and touch already scroll these; a mouse has no equivalent gesture,
 * so without this a mouse user is left with the arrow buttons alone.
 *
 * Applied to any element carrying data-bb-drag. Three things it is careful about:
 *
 *   · Mouse only. Touch and pen already scroll natively, and hijacking those
 *     pointers would replace a good gesture with a worse one.
 *   · Scroll snapping is suspended during the drag. With snap-type mandatory
 *     the browser fights every frame of a manual scrollLeft, and the rail
 *     judders instead of following the cursor.
 *   · A drag that ends on a link must not follow it. The click is swallowed
 *     once, in the capture phase, when the pointer actually travelled.
 */
const THRESHOLD = 4;

class DragScroll {
  constructor(el) {
    this.el = el;
    this.el.addEventListener('pointerdown', (e) => this.#down(e));
    this.el.classList.add('bb-draggable');
    // Swallow the click that ends a drag, before it reaches the link.
    this.el.addEventListener('click', (e) => {
      if (!this.moved) return;
      e.preventDefault();
      e.stopPropagation();
      this.moved = false;
    }, true);
  }

  #down(e) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    this.startX = e.clientX;
    this.startLeft = this.el.scrollLeft;
    this.moved = false;
    this.snap = this.el.style.scrollSnapType;
    this.dragging = true;
    this.el.classList.add('is-dragging');

    this.onMove = (ev) => this.#move(ev);
    this.onUp = (ev) => this.#up(ev);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
  }

  #move(e) {
    if (!this.dragging) return;
    const dx = e.clientX - this.startX;
    if (!this.moved && Math.abs(dx) > THRESHOLD) {
      this.moved = true;
      // Only now suspend snapping: a plain click should leave it alone.
      this.el.style.scrollSnapType = 'none';
    }
    if (!this.moved) return;
    e.preventDefault();
    this.el.scrollLeft = this.startLeft - dx;
  }

  #up() {
    if (!this.dragging) return;
    this.dragging = false;
    this.el.classList.remove('is-dragging');
    this.el.style.scrollSnapType = this.snap ?? '';
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
    // `moved` is cleared by the click handler, or here when no click follows.
    setTimeout(() => { this.moved = false; }, 0);
  }
}

const attach = (root = document) => {
  for (const el of root.querySelectorAll('[data-bb-drag]')) {
    if (el.dataset.bbDragBound) continue;
    el.dataset.bbDragBound = '1';
    new DragScroll(el);
  }
};

attach();
// Sections re-render in the theme editor and on some navigations.
document.addEventListener('shopify:section:load', (e) => attach(e.target));
